import "@/App.css";
import "@/refinements.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import Discover from "@/pages/Discover";
import Games from "@/pages/Games";
import GameHub from "@/pages/GameHub";
import Booking from "@/pages/Booking";
import Checkout from "@/pages/Checkout";
import Dashboard from "@/pages/Dashboard";
import PerformAI from "@/pages/PerformAI";
import Events from "@/pages/Events";
import Guides from "@/pages/Guides";
import Communities from "@/pages/Communities";
import Messages from "@/pages/Messages";
import Wallet from "@/pages/Wallet";
import AdminConsole from "@/admin/pages/AdminConsole";
import Auth from "@/pages/Auth";
import AuthVerify from "@/pages/AuthVerify";
import Onboarding from "@/pages/Onboarding";
import SmartMatch from "@/pages/SmartMatch";
import VodAnalysis from "@/pages/VodAnalysis";
import LiveRoom from "@/pages/LiveRoom";
import Search from "@/pages/Search";
import { Docs, Developers } from "@/pages/site/docs";
import Changelog from "@/pages/site/changelog";
import Coach from "@/pages/site/coach";
import Custom from "@/pages/site/custom";
import Cotisation from "@/pages/site/cotisation";
import Status from "@/pages/site/status";
import { HelpCenter, HelpArticle } from "@/pages/site/help";
import { LegalIndex, LegalDoc } from "@/pages/site/legal";
import {
  AITransparency, Compliance, Disclosure, PrivacyCenter,
  Security, Subprocessors, TrustCenter,
} from "@/pages/site/trust";
import {
  About, Audience, Brand, Careers, Community, Contact,
  Experts, Features, Integrations, Manifesto, Partners, Press, Pricing,
  Roadmap, Testimonials, FeatureDetail,
} from "@/pages/site/simple";

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Page publique, hors de l'espace de travail (pas de sidebar). */}
          <Route path="/" element={<Landing />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/auth/verifier" element={<AuthVerify />} />
          <Route path="/onboarding" element={<Onboarding />} />

          {/* Console d'administration : coquille dédiée, hors espace joueur.
              La garde client est du confort ; l'autorisation réelle est serveur. */}
          <Route element={<ProtectedRoute admin skipOnboarding />}>
            <Route path="/admin/*" element={<AdminConsole />} />
          </Route>

          {/* Pages publiques du site : nav et footer propres, hors espace de travail. */}
          <Route path="/fonctionnalites" element={<Features />} />
          <Route path="/fonctionnalites/:feature" element={<FeatureDetail />} />
          <Route path="/tarifs" element={<Pricing />} />
          <Route path="/pour/:segment" element={<Audience />} />
          <Route path="/integrations" element={<Integrations />} />
          <Route path="/roadmap" element={<Roadmap />} />
          <Route path="/nouveautes" element={<Changelog />} />
          <Route path="/developpeurs" element={<Developers />} />
          <Route path="/docs/*" element={<Docs />} />
          <Route path="/docs" element={<Docs />} />
          <Route path="/aide" element={<HelpCenter />} />
          <Route path="/aide/:slug" element={<HelpArticle />} />
          <Route path="/statut" element={<Status />} />

          <Route path="/trust-center" element={<TrustCenter />} />
          <Route path="/securite" element={<Security />} />
          <Route path="/securite/divulgation" element={<Disclosure />} />
          <Route path="/conformite" element={<Compliance />} />
          <Route path="/confidentialite" element={<PrivacyCenter />} />
          <Route path="/ia" element={<AITransparency />} />

          <Route path="/legal" element={<LegalIndex />} />
          <Route path="/legal/sous-traitants" element={<Subprocessors />} />
          <Route path="/legal/:slug" element={<LegalDoc />} />

          <Route path="/a-propos" element={<About />} />
          <Route path="/manifeste" element={<Manifesto />} />
          <Route path="/carrieres" element={<Careers />} />
          <Route path="/presse" element={<Press />} />
          <Route path="/partenaires" element={<Partners />} />
          <Route path="/experts" element={<Experts />} />
          <Route path="/communaute" element={<Community />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/marque" element={<Brand />} />
          <Route path="/coach" element={<Coach />} />
          <Route path="/custom" element={<Custom />} />
          <Route path="/cotisation" element={<Cotisation />} />
          <Route path="/temoignages" element={<Testimonials />} />


          <Route element={<Layout />}>
            {/* Public */}
            <Route path="/discover" element={<Discover />} />
            <Route path="/games" element={<Games />} />
            <Route path="/games/:slug" element={<GameHub />} />
            <Route path="/events" element={<Events />} />
            <Route path="/guides" element={<Guides />} />
            <Route path="/communities" element={<Communities />} />
            <Route path="/search" element={<Search />} />

            {/* Connecté */}
            <Route element={<ProtectedRoute />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/sessions" element={<Dashboard view="sessions" />} />
              <Route path="/progression" element={<Dashboard view="progression" />} />
              <Route path="/booking/:username" element={<Booking />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/messages" element={<Messages />} />
              <Route path="/wallet" element={<Wallet />} />
              <Route path="/perform-ai" element={<PerformAI />} />
              <Route path="/smart-match" element={<SmartMatch />} />
              <Route path="/vod" element={<VodAnalysis />} />
              <Route path="/live/:sessionId" element={<LiveRoom />} />
            </Route>

          </Route>

          {/* Anciennes adresses de la place de marché : un seul coach désormais. */}
          <Route path="/kustom" element={<Navigate to="/custom" replace />} />
          <Route path="/coaches" element={<Navigate to="/coach" replace />} />
          <Route path="/coach/:username" element={<Navigate to="/coach" replace />} />
          <Route path="/become-coach" element={<Navigate to="/tarifs" replace />} />
          <Route path="/ressources-coach" element={<Navigate to="/coach" replace />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster theme="light" position="top-right" richColors />
    </AuthProvider>
  );
}
